<script lang="ts">
	/**
	 * One dimension, ranked (§12.10).
	 *
	 * **Horizontal bars, not a pie.** The question is "which of these is biggest and by how much",
	 * and a bar's length is read accurately where a wedge's angle is not — and a dimension with
	 * fourteen values is a legend hunt as a pie and an ordinary list as bars.
	 *
	 * **One hue for every bar.** The bars are one series measured on one scale; giving each a
	 * different colour would say the categories differed in kind as well as in size, and it would
	 * spend the categorical palette on a distinction the axis labels already make.
	 *
	 * Sorted descending by the server, and the sort is *not* what picks the colour — that rule
	 * matters wherever a colour is per-entity, and here it is trivially satisfied by there being
	 * one.
	 */

	import type { AnalyticsBreakdownRow } from '../../AppTypes';
	import { baseChartOptions, chartTheme } from './chartTheme';
	import ApexChart from './ApexChart.svelte';

	export let rows: AnalyticsBreakdownRow[] = [];
	export let title: string = '';
	/** Which column the bar length means. Sessions for an audience, events for a page or product. */
	export let measure: 'sessions' | 'events' | 'purchases' = 'sessions';
	export let height: number = 0;

	$: theme = chartTheme();

	/** A bar reading "Unknown" is a real answer — an absent country, a direct visit with no referrer. */
	$: labels = rows.map(row => (row.label.length === 0 ? 'Unknown' : row.label));
	$: values = rows.map(row =>
		measure === 'events' ? row.events : measure === 'purchases' ? (row.purchases ?? 0) : row.sessions
	);

	/* Height from the row count rather than fixed: fourteen bars in a 280px box is four-pixel bars. */
	$: resolvedHeight = height > 0 ? height : Math.max(160, 34 * rows.length + 40);

	$: options = {
		...baseChartOptions(theme, resolvedHeight),
		chart: { ...(baseChartOptions(theme, resolvedHeight).chart as object), type: 'bar' },
		series: [{ name: title, data: values }],
		colors: [theme.single],
		plotOptions: {
			bar: {
				horizontal: true,
				// Thin marks with a 4px rounded data end, anchored to the baseline: the rounding is
				// on the value end only, so the bar still starts flat at zero.
				barHeight: '62%',
				borderRadius: 4,
				borderRadiusApplication: 'end',
				dataLabels: { position: 'center' }
			}
		},
		// One series, so the value is written on the bar rather than hunted for on an axis.
		dataLabels: {
			enabled: true,
			style: { fontSize: '11px', colors: [theme.primaryInk], fontWeight: 500 },
			formatter: (value: number) => String(value)
		},
		xaxis: { ...(baseChartOptions(theme, resolvedHeight).xaxis as object), categories: labels },
		legend: { show: false },
		grid: {
			...(baseChartOptions(theme, resolvedHeight).grid as object),
			// Vertical lines only: a horizontal bar chart's category axis needs no gridline, and one
			// per bar is a ladder behind the data.
			xaxis: { lines: { show: true } },
			yaxis: { lines: { show: false } }
		}
	};
</script>

<ApexChart {options} label={`${title}, ranked`}>
	<svelte:fragment slot="fallback">
		<p class="text-default text-sm py-4">
			This chart could not be drawn. The same figures are in the table below.
		</p>
	</svelte:fragment>
</ApexChart>
